/**
 * @author Muhammad Fahad
 * @datetime April, 20th 2022
 * @description: This is component shows floor form and related methods
 */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription, Subject } from 'rxjs';
import { SiteManagementService } from 'src/app/site-management/site-management.service';
import { SiteManagerComponent } from 'src/app/site-management/site-manager/site-manager.component';
import { FloorManagementService } from '../floor-management.service';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { PageName } from 'src/app/shared/utils/user-roles';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { IFloor } from '../floor-management-interface';
import { Utility } from 'src/app/shared/utils/utility.service';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { ConfirmDialogComponent } from 'src/app/shared-layout/Components/confirm-dialog/confirm-dialog.component';
import { ConfirmRemoveDialogComponent } from 'src/app/shared-layout/Components/confirm-remove-dialog/confirm-remove-dialog.component';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { CommonService } from 'src/app/shared/utils/common.service';
import { RestrictRemoveDialogComponent } from 'src/app/shared-layout/Components/restrict-remove-dialog/restrict-remove-dialog.component';
import { StateManagementService } from 'src/app/shared/services/state-management.service';
import { config } from 'src/config/config';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-add-floor-details',
  templateUrl: './add-floor-details.component.html',
  styleUrls: ['./add-floor-details.component.scss'],
})
export class AddFloorDetailsComponent implements OnInit, OnDestroy {
  site: any;
  addFloorForm: any;
  file: any;
  buildingPhotoKey: string | undefined;
  buildingPhotoURL: string | null | undefined;
  floors: any = [];
  floorimage = 'assets/svg/ico-floors-circle.svg';
  subscription: Subscription | undefined;
  buildingName!: string;
  sharedData!: any;
  floor: any;
  readonly LANG_CONST = LANG_CONST;
  readonly config = config;
  PageName = PageName;
  CONSTANTS = CONSTANTS;
  previousData?: IFloor;
  commonAccessControl!: boolean;

  public ngDestroyed$ = new Subject();
  @Input() selectedSite: any;
  @Input() selectedBuilding: any;
  @Input() resetNavigation: any;
  @Input() navigateToAddBuilding: any;
  @Input() navigateToAddFloor: any;
  @Input() enableBackOperation: any;
  @Input() navigateConfirmDialog: any;
  @Input() closeConfirmDialog: any;
  result: boolean = false;
  localFloors: any;
  sharedDataSubscription!: Subscription;
  unitCountLabel: string = this.translate.instant('NO_OF_UNITS_PER_FLOOR');
  accessPointCountLabel: string = this.translate.instant(
    'NO_OF_ACCESSPOINTS_PER_FLOOR'
  );

  /**
   * @description injecting services to the constructor
   * @param { MatDialogRef<SiteManagerComponent> } dialogRef
   * @param { FloorManagementService } floorManagementService
   * @param { SharedDataService } sharedDataService
   */
  constructor(
    public dialogRef: MatDialogRef<SiteManagerComponent>,
    private floorManagementService: FloorManagementService,
    private sharedDataService: SharedDataService,
    private helperService: HelperService,
    private notificationService: NotificationService,
    private commonService: CommonService,
    private stateManagementService: StateManagementService,
    private translate: TranslateService
  ) {}

  /**
   * @description subscribe shared data service and calls getAllFloorsByBuildingId API
   */
  ngOnInit(): void {
    this.addFloorForm = new FormGroup({
      floorName: new FormControl(
        null,
        MFValidators.compose([
          MFValidators.required,
          MFValidators.minLength(1),
          MFValidators.maxLength(16),
          MFValidators.whitespace,
          MFValidators.alphaNumWithRequiredSpecialCharacters,
        ])
      ),
      wingName: new FormControl(
        null,
        MFValidators.compose([
          MFValidators.nullValidator,
          MFValidators.maxLength(50),
          MFValidators.whitespace,
          MFValidators.entityNameV2,
        ])
      ),
      accessPointsCount: new FormControl(null, [
        MFValidators.min(1),
        MFValidators.max(1000),
      ]),
      unitsCount: new FormControl(null, [
        MFValidators.min(0),
        MFValidators.max(1000),
      ]),
    });

    this.sharedDataSubscription = this.sharedDataService.currentData.subscribe(
      (sharedData: any) => {
        this.sharedData = sharedData;
        this.commonAccessControl =
          this.sharedData.selectedSite.commonAccessControl;
      }
    );

    if (this.sharedData.editMode.floor == true) {
      this.floorManagementService
        .getAllFloorsByBuildingId(
          this.sharedData.selectedSite.siteId,
          this.sharedData.selectedBuilding.buildingId,
          true
        )
        .subscribe({
          next: (res: any) => {
            const floors = res;
            this.floor = floors?.find(
              (floor: any) =>
                floor?.floorId === this.sharedData?.selectedFloor?.floorId
            );
            this.addFloorForm.patchValue({
              floorName: this.floor?.floorName,
              wingName: this?.floor?.wingName ? this?.floor?.wingName : '',
            });
            this.previousData = {
              floorId: this.floor?.floorId,
              floorName: this.floor?.floorName,
              wingName: this?.floor?.wingName || '',
              accessPointsCount: this?.floor?.accessPointsCount,
              unitsCount: this?.floor?.unitsCount,
            };
            if (this?.floor?.unitsCount && this?.floor?.unitsCount !== 0) {
              this.addFloorForm?.controls['unitsCount'].setValidators([
                MFValidators.min(this.previousData?.unitsCount),
                MFValidators.required,
                MFValidators.max(1000),
              ]);
              this.addFloorForm.patchValue({
                unitsCount: this?.floor?.unitsCount,
              });
              this.unitCountLabel = this.translate.instant(
                'NO_OF_UNITS_PER_FLOOR_ASTERIK'
              );
            }
            if (
              this?.floor?.accessPointsCount &&
              this?.floor?.accessPointsCount !== 0
            ) {
              this.addFloorForm?.controls['accessPointsCount'].setValidators([
                MFValidators.min(this.previousData?.accessPointsCount),
                MFValidators.required,
                MFValidators.max(1000),
              ]);
              this.addFloorForm.patchValue({
                accessPointsCount: this?.floor?.accessPointsCount,
              });
              this.accessPointCountLabel = this.translate.instant(
                'NO_OF_ACCESSPOINTS_PER_FLOOR_ASTERIK'
              );
            }
          },
          error: (err: Error) => {
            this.notificationService.showError(
              this.translate.instant('FAILED_TO_LOAD_FLOORS_OF_THIS_BUILDING')
            );
          },
        });
    } else {
      this.localFloors = this.stateManagementService.getFloors();
    }
    if (!this.sharedData.selectedSite.commonAccessControl) {
      this.addFloorForm?.controls['accessPointsCount'].clearValidators();
    }
  }

  /**
   * @description unsubscribe the shared data subscription
   */
  ngOnDestroy(): void {
    this.ngDestroyed$.next(void 0);
    this.ngDestroyed$.complete();
    this.sharedDataSubscription.unsubscribe();
  }

  /**
   * @description this method closes dialog
   */
  closeDialog() {
    let floorName = this.addFloorForm?.controls.floorName?.value;
    let wingName = this.addFloorForm?.controls.wingName?.value;
    let accessPointsCount =
      this.addFloorForm?.controls.accessPointsCount?.value;
    let unitsCount = this.addFloorForm?.controls.unitsCount?.value;

    let currentData: IFloor = {
      floorId: this.previousData?.floorId || '',
      floorName: floorName,
      wingName: wingName,
      accessPointsCount: accessPointsCount,
      unitsCount: unitsCount,
    };

    if (
      (this.sharedData.editMode.floor &&
        Utility.compareJson(this.previousData || {}, currentData || {})) ||
      (!this.sharedData.editMode.floor &&
        this.validateEmpty(floorName, wingName, accessPointsCount, unitsCount))
    ) {
      this.resetNavigation();
      this.dialogRef.close(this.result);
      return;
    }

    this.navigateConfirmDialog();
    const dialogInstance = this.helperService.openModalDialog(
      ConfirmDialogComponent,
      {
        title: this.translate.instant('TITLE'),
        headingText: this.translate.instant('HEADING_TEXT'),
        bodyText: this.translate.instant('BODY_TEXT'),
        footerText: this.translate.instant('FOOTER_TEXT'),
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
    dialogInstance.afterClosed().subscribe((result: boolean) => {
      this.closeConfirmDialog();
      if (result) {
        this.resetNavigation();
        this.dialogRef.close(this.result);
      }
    });
  }

  /**
   * @description navigates back to the add/edit building screen
   */
  navigateBack() {
    let floorName = this.addFloorForm?.controls.floorName?.value;
    let wingName = this.addFloorForm?.controls.wingName?.value;
    let accessPointsCount =
      this.addFloorForm?.controls.accessPointsCount?.value;
    let unitsCount = this.addFloorForm?.controls.unitsCount?.value;

    let currentData: IFloor = {
      floorId: this.previousData?.floorId || '',
      floorName: floorName,
      wingName: wingName,
      accessPointsCount: accessPointsCount,
      unitsCount: unitsCount,
    };

    if (
      (this.sharedData.editMode.floor &&
        Utility.compareJson(this.previousData || {}, currentData || {})) ||
      (!this.sharedData.editMode.floor &&
        this.validateEmpty(floorName, wingName, accessPointsCount, unitsCount))
    ) {
      this.sharedDataService.setEditMode({
        building: true,
        floor: false,
      });
      this.navigateToAddBuilding(this.result);
      return;
    }

    this.navigateConfirmDialog();
    const dialogInstance = this.helperService.openModalDialog(
      ConfirmDialogComponent,
      {
        title: this.translate.instant('TITLE'),
        headingText: this.translate.instant('HEADING_TEXT'),
        bodyText: this.translate.instant('BODY_TEXT'),
        footerText: this.translate.instant('FOOTER_TEXT'),
      },
      CONSTANTS.FIXED_SCREEN_MODAL
    );
    dialogInstance.afterClosed().subscribe((result: boolean) => {
      this.closeConfirmDialog();
      if (result) {
        this.sharedDataService.setEditMode({
          building: true,
          floor: false,
        });
        this.navigateToAddBuilding(this.result);
      }
    });
  }

  /**
   * @description this method is called when Add Floor button is clicked.
   *               this method adds new floor within the building
   */
  onAddFlooHandler() {
    let siteId = this.sharedData.selectedSite.siteId;
    let buildingId = this.sharedData.selectedBuilding.buildingId;
    let buildingName = this.sharedData.selectedBuilding.buildingName;
    let floorName = this.addFloorForm?.controls.floorName?.value;
    let wingName = this.addFloorForm?.controls.wingName?.value;
    let accessPointsCount =
      this.addFloorForm?.controls.accessPointsCount?.value;
    let unitsCount = this.addFloorForm?.controls.unitsCount?.value;
    this.floorManagementService
      .addFloor(
        siteId,
        buildingId,
        floorName,
        unitsCount,
        wingName,
        accessPointsCount
      )
      .subscribe({
        next: (res: any) => {
          let obj = {
            siteId: siteId,
            buildingId: buildingId,
            buildingName: buildingName,
            floorId: res.floorId,
            floorName: floorName,
            wingName: wingName,
            accessPointsCount: accessPointsCount,
            unitsCount: unitsCount,
          };
          this.floors.push(obj);
          this.addFloorForm.reset();
          this.addFloorForm.markAsUntouched();
          this.result = true;

          this.notificationService.showSuccess(
            this.commonService.stringInterPolation(
              this.translate.instant('SUCCESSFULLY_CREATED'),
              this.translate.instant('FLOOR')
            )
          );
        },
        error: (err: Error) => {
          this.notificationService.showError(err?.message);
        },
      });
  }

  /**
   * @description this method is called when Done button is clicked.
   *              this method adds new floor within the building or update
   *              existing floor depends on edit mood
   */
  onDoneHandler() {
    const floor: any = {
      siteId: this.sharedData.selectedSite.siteId,
      buildingId: this.sharedData.selectedBuilding.buildingId,
      floorId: this.previousData?.floorId,
      floorName: this.addFloorForm?.controls.floorName?.value,
      unitsCount: this.addFloorForm?.controls.unitsCount?.value,
      wingName: this.addFloorForm?.controls.wingName?.value,
    };

    if (
      this.commonAccessControl &&
      this.addFloorForm?.controls.accessPointsCount?.value
    ) {
      floor.accessPointsCount =
        this.addFloorForm?.controls.accessPointsCount?.value;
    }

    if (this?.sharedData?.editMode?.floor) {
      let currentData: IFloor = {
        floorId: this.previousData?.floorId || '',
        floorName: floor?.floorName,
        wingName: floor?.wingName,
        accessPointsCount: floor?.accessPointsCount,
        unitsCount: floor?.unitsCount,
      };

      if (
        !this.previousData?.floorId ||
        Utility.compareJson(this.previousData || {}, currentData || {})
      ) {
        this.sharedDataService.setEditMode({
          building: true,
          floor: false,
        });
        this.navigateToAddBuilding();
        return;
      }

      // calling floor management service to update floor.
      this.floorManagementService.updateFloor(floor).subscribe({
        next: (res: any) => {
          this.sharedDataService.setEditMode({
            building: true,
            floor: false,
          });
          this.result = true;
          this.floorManagementService.setTriggerRerender(true); // this triger will fire parent componet for updating building list
          this.navigateToAddBuilding(this.result);
          this.notificationService.showSuccess(
            this.translate.instant('SUCCESSFULLY_UPDATED')
          );
        },
        error: (err: Error) => {
          this.notificationService.showError(err?.message);
        },
      });
    } else {
      // calling floor management service to add new floor.
      this.floorManagementService
        .addFloor(
          floor?.siteId,
          floor?.buildingId,
          floor?.floorName,
          floor?.unitsCount,
          floor?.wingName,
          floor?.accessPointsCount
        )
        .subscribe({
          next: (res: any) => {
            this.sharedDataService.setEditMode({
              building: true,
              floor: false,
            });
            this.result = true;
            this.navigateToAddBuilding(this.result);

            this.notificationService.showSuccess(
              this.commonService.stringInterPolation(
                this.translate.instant('SUCCESSFULLY_CREATED'),
                this.translate.instant('FLOOR')
              )
            );
          },
          error: (err: Error) => {
            this.notificationService.showError(err?.message);
          },
        });
    }
  }

  /**
   * @description this method is called when Remove button is clicked.
   *              this method calls delete floor API to remove floor from the building
   */
  onRemoveFloor() {
    if (this?.floor?.accessPointsCount + this?.floor?.unitsCount > 0) {
      this.helperService.openModalDialog(
        RestrictRemoveDialogComponent,
        {
          title: this.translate.instant('DELETE_FLOOR'),
          subTitle: this.commonService.stringInterPolation(
            this.translate.instant('UNABLE_TO_REMOVE_FLOOR'),
            this.floor?.floorName
          ),
          bodyText: this.commonService.stringInterPolation(
            this.translate.instant('RESTRICT_REMOVE_FLOOR'),
            this.floor?.floorName
          ),
          footerText: !this.commonAccessControl
            ? ''
            : this.translate.instant(
                'TO_REMOVE_DEVICES_FROM_AN_ACCESS_POINT_YOU_MUST_USE_THE_PROPERTY_MANAGER_MOBILE'
              ),
        },
        CONSTANTS.FIXED_SCREEN_MODAL
      );
    } else {
      this.navigateConfirmDialog();
      const dialogInstance = this.helperService.openModalDialog(
        ConfirmRemoveDialogComponent,
        {
          title: this.translate.instant('REMOVE_FLOOR'),
          bodyText: this.translate.instant(
            'ARE_YOU_SURE_YOU_WANT_TO_REMOVE_FLOOR'
          ),
          footerText: this.translate.instant(
            'BY_MOVING_FORWARD_YOU_WILL_LOSE_ALL_INFO_FLOOR'
          ),
        },
        CONSTANTS.FIXED_SCREEN_MODAL
      );
      dialogInstance.afterClosed().subscribe((result: boolean) => {
        this.closeConfirmDialog();
        if (result) {
          this.floorManagementService
            .deleteFloor(
              this.sharedData?.selectedFloor?.floorId,
              this.sharedData?.selectedSite?.siteId,
              this.sharedData?.selectedBuilding?.buildingId
            )
            .subscribe({
              next: (res: any) => {
                if (res?.floorId == this?.sharedData?.selectedFloor?.floorId) {
                  this.sharedDataService?.setSelectedFloor({
                    floorId: null,
                    floorName: null,
                  });
                  this.sharedDataService?.setEditMode({
                    building: true,
                    floor: false,
                  });
                  this.result = true;
                  this.notificationService.showSuccess(
                    this.commonService.stringInterPolation(
                      this.translate.instant('SUCCESSFULLY_DELETED'),
                      this.translate.instant('FLOOR')
                    )
                  );
                  this.navigateToAddBuilding(this.result);
                }
              },
              error: (err: Error) => {
                this.notificationService.showError(err?.message);
              },
            });
        }
      });
    }
  }

  /**
   * @description this validates input
   * @param { any } floorName
   * @param { any } wingName
   * @param { any } accessPoints
   * @param { any } unitsCount
   */
  validateEmpty(
    floorName: any,
    wingName: any,
    accessPointsCount: any,
    unitsCount: any
  ) {
    if (
      (floorName === null || floorName === undefined) &&
      (wingName === null || wingName === undefined)
    )
      return true;
    return false;
  }
}
